import { Flex, Paper, Text, Badge, Box, SimpleGrid, Stack } from "@mantine/core";
import {
  IconBuilding,
  IconMapPin,
  IconTool,
  IconUser,
  IconShieldCheck,
  IconBriefcase,
  IconTarget,
  IconUserCheck,
} from "@tabler/icons-react";
import S3Image from "@/components/shared/S3Image";
import ProductsServicesTab from "@/components/shared/AnonymousCompanyProfile/ProductsServicesTab";
import {
  getIndustryLabel,
  getSubIndustryLabel,
  getCollaborationIntentLabel,
  getCollaborationRoleLabel,
  getWhoAmILabel,
  getAdditionalTechInfraLabel,
  FileCategory,
  regionsData,
} from "@/components/shared/constants/general.const";
import { formatVerificationBusinessTypeLabel } from "@/pages/EmptyCompanyProfile/EmptyProfileForms/verificationDocuments";
import { displayHeadquartersText } from "@/shared/constants/signup.const";

interface MatchedProfileTabProps {
  company: any;
  collaboration?: any;
}

export default function MatchedProfileTab({
  company,
  collaboration,
}: MatchedProfileTabProps) {
  const getRegionLabel = (regionId: string): string => {
    const region = regionsData.regions.find((r) => r.id === regionId);
    return region ? region.name : regionId;
  };

  const metadata = company.externalVerificationLogs?.[0]?.metadata;

  return (
    <>
      {/* Collaboration Intent Details */}
      {collaboration?.requesterRole && (
        <Paper
          p={0}
          radius="lg"
          mb="xl"
          style={{
            overflow: "hidden",
            backgroundColor: "#ebf0ff",
            boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Flex
            align="center"
            gap="sm"
            px={{ base: "md", sm: "xl" }}
            py={{ base: "md", sm: "lg" }}
            style={{ backgroundColor: "#04305c" }}
          >
            <IconBriefcase size={22} color="#ffc300" stroke={1.5} />
            <Text fw={700} fz="md" c="#ffc300" style={{ letterSpacing: 0.2 }}>
              Collaboration Intent Details
            </Text>
          </Flex>
          <Box p={{ base: "lg", sm: "xl" }}>
            <Text size="xs" c="slate.5" mb="lg">
              Information provided by the company about their collaboration goals
            </Text>

            <Flex direction="column" gap="md">
            {/* Collaboration type they're exploring */}
            {Array.isArray(company.collaborationIntent) && company.collaborationIntent.length > 0 && (
              <Box>
                <Flex align="center" gap={6} mb="xs">
                  <IconTarget size={14} color="var(--mantine-color-violet-6)" />
                  <Text size="xs" c="slate.6" tt="uppercase" fw={400}>
                    Collaboration type they're exploring
                  </Text>
                </Flex>
                <Flex gap="xs" wrap="wrap">
                  {company.collaborationIntent.map((intent: string, i: number) => (
                    <Badge
                      key={i}
                      variant="light"
                      color="#E9D4FF"
                      bg="#FAF5FF"
                      c="#8200DB"
                      py={10}
                      px={8}
                      size="md"
                      fw={400}
                      style={{ border: "1px solid #E9D4FF" }}
                    >
                      {getCollaborationIntentLabel(intent)}
                    </Badge>
                  ))}
                </Flex>
              </Box>
            )}

            {/* Their role in this collaboration */}
            <Box>
              <Flex align="center" gap={6} mb="xs">
                <IconBriefcase size={14} color="var(--mantine-color-teal-6)" />
                <Text size="xs" c="slate.6" tt="uppercase" fw={400}>
                  Their role in this collaboration
                </Text>
              </Flex>
              <Flex gap="xs" wrap="wrap">
                <Badge
                  variant="light"
                  color="#B9F8CF"
                  bg="#F0FDF4"
                  c="#008236"
                  py={10}
                  px={8}
                  size="md"
                  fw={400}
                  style={{ border: "1px solid #B9F8CF" }}
                >
                  {getCollaborationRoleLabel(collaboration.requesterRole)}
                </Badge>
              </Flex>
            </Box>

            {/* What they expect from you */}
            {collaboration.expectedPartnerRole && (
              <Box>
                <Flex align="center" gap={6} mb="xs">
                  <IconUserCheck size={14} color="var(--mantine-color-yellow-7)" />
                  <Text size="xs" c="slate.6" tt="uppercase" fw={400}>
                    What they expect from you
                  </Text>
                </Flex>
                <Flex gap="xs" wrap="wrap">
                  <Badge
                    variant="light"
                    color="#FEE685"
                    bg="#FFFBEB"
                    c="#BB4D00"
                    py={10}
                    px={8}
                    size="md"
                    fw={400}
                    style={{ border: "1px solid #FEE685" }}
                  >
                    {getCollaborationRoleLabel(collaboration.expectedPartnerRole)}
                  </Badge>
                </Flex>
              </Box>
            )}
            </Flex>
          </Box>
        </Paper>
      )}

      {/* Business Overview */}
      <Paper
        p={0}
        radius="lg"
        mb="xl"
        style={{
          overflow: "hidden",
          backgroundColor: "#ebf0ff",
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Flex
          align="center"
          gap="sm"
          px={{ base: "md", sm: "xl" }}
          py={{ base: "md", sm: "lg" }}
          style={{ backgroundColor: "#04305c" }}
        >
          <IconBuilding size={22} color="#ffc300" stroke={1.5} />
          <Text fw={700} fz="md" c="#ffc300" style={{ letterSpacing: 0.2 }}>
            Business Overview
          </Text>
        </Flex>
        <Box p={{ base: "lg", sm: "xl" }}>
        <Flex direction="column" gap="lg">

        <Flex style={{ flex: "1 1 100%" }} justify="space-between" gap="md" wrap="wrap">
              <Flex style={{ flex: "1 1 48%", minWidth: "280px" }} gap="md" wrap="wrap">
                <Flex direction="column" style={{ flex: "1 1 46%", minWidth: "220px" }}>
                <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                COMPANY DESCRIPTION
              </Text>
              <Text size="sm" style={{ lineHeight: 1.5, wordBreak: "break-word", overflow: "hidden", textAlign: "justify" }}>
                {company.description || "N/A"}
                </Text>
                </Flex>

                </Flex>

              <Flex style={{ flex: "1 1 48%", minWidth: "280px" }} gap="md" wrap="wrap">
                <Flex direction="column" style={{ flex: "1 1 46%", minWidth: "220px" }}>
                <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                COUNTRY
              </Text>
              <Text size="sm">{displayHeadquartersText(company.headquartersCountry)}</Text>
                </Flex>

                <Flex direction="column" style={{ flex: "1 1 46%", minWidth: "220px" }}>
                <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                BUSINESS ID
              </Text>
              <Text size="sm">{company.gtid || company.registrationNumber || "N/A"}</Text>
                </Flex>
              </Flex>
            </Flex>

          {/* Two Column Layout */}
          <Flex gap="xl" wrap="wrap">

            {/* <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="xs" fw={400}>
                YEARS OF OPERATION
              </Text>
              <Text size="sm">{getYearsOfOperation()}</Text>
            </Flex> */}

            <Flex style={{ flex: "1 1 100%" }} justify="space-between" gap="md" wrap="wrap">
              <Flex style={{ flex: "1 1 48%", minWidth: "280px" }} gap="md" wrap="wrap">
                <Flex direction="column" style={{ flex: "1 1 46%", minWidth: "220px" }}>
                <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                TRADE NAME
              </Text>
              <Text size="sm">{company.tradeName || "N/A"}</Text>
                </Flex>

                <Flex direction="column" style={{ flex: "1 1 46%", minWidth: "220px" }}>
                   <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                BUSINESS STRUCTURE
              </Text>
              <Text size="sm">
                {formatVerificationBusinessTypeLabel(company.additionalDetails?.verificationBusinessType)}
              </Text>
                </Flex>
              </Flex>

              <Flex style={{ flex: "1 1 48%", minWidth: "280px" }} gap="md" wrap="wrap">
                <Flex direction="column" style={{ flex: "1 1 46%", minWidth: "220px" }}>
                <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                PAID-UP CAPITAL BAND (USD)
              </Text>
              <Text size="sm">{company.additionalDetails?.paidupCapitalBand?.trim() || "N/A"}</Text>
                </Flex>

                <Flex direction="column" style={{ flex: "1 1 46%", minWidth: "220px" }}>
                <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                TURNOVER BAND (USD)
              </Text>
              <Text size="sm">{company.additionalDetails?.turnoverBand?.trim() || "N/A"}</Text>
                </Flex>
              </Flex>
            </Flex>

            <Flex style={{ flex: "1 1 100%" }} justify="space-between" gap="md" wrap="wrap">
              <Flex style={{ flex: "1 1 48%", minWidth: "280px" }} gap="md" wrap="wrap">
                <Flex direction="column" style={{ flex: "1 1 46%", minWidth: "220px" }}>
                  <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                    INDUSTRY CATEGORY
                  </Text>
                  <Text size="sm">
                    {Array.isArray(company.industryCategory) && company.industryCategory.length > 0
                      ? company.industryCategory.map((c: string) => getIndustryLabel(c)).join(", ")
                      : "N/A"}
                  </Text>
                </Flex>

                <Flex direction="column" style={{ flex: "1 1 46%", minWidth: "220px" }}>
                  <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                    SUB INDUSTRY
                  </Text>
                  <Text size="sm">
                    {Array.isArray(company.subIndustry) && company.subIndustry.length > 0
                      ? company.subIndustry.map((sub: string) => getSubIndustryLabel(sub)).join(", ")
                      : "N/A"}
                  </Text>
                </Flex>
              </Flex>

              <Flex style={{ flex: "1 1 48%", minWidth: "280px" }} gap="md" wrap="wrap">
                <Flex direction="column" style={{ flex: "1 1 46%", minWidth: "220px" }}>
                  <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                    WHO AM I
                  </Text>
                  <Text size="sm">
                    {Array.isArray(company.whoAmI) && company.whoAmI.length > 0
                      ? company.whoAmI.map((role: string) => getWhoAmILabel(role)).join(", ")
                      : "N/A"}
                  </Text>
                </Flex>

                <Flex direction="column" style={{ flex: "1 1 46%", minWidth: "220px" }}>
                  <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                    COLLABORATION INTENT
                  </Text>
                  <Text size="sm">
                    {Array.isArray(company.collaborationIntent) && company.collaborationIntent.length > 0
                      ? company.collaborationIntent.map((intent: string) => getCollaborationIntentLabel(intent)).join(", ")
                      : "N/A"}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Flex>

          {/* Company Gallery */}
          {company.images &&
            company.images.filter((img: any) => img.category === FileCategory.COMPANY_IMAGE).length > 0 && (
              <Box>
                <Text size="xs" c="slate.6" tt="uppercase" mb="sm" fw={400}>
                  COMPANY GALLERY
                </Text>
                <Flex gap="sm" wrap="wrap">
                  {company.images
                    .filter((img: any) => img.category === FileCategory.COMPANY_IMAGE)
                    .map((image: any, index: number) => (
                      <Box key={index} style={{ width: 100, height: 100 }}>
                        <S3Image
                          fileId={image.id}
                          alt={`Company image ${index + 1}`}
                          radius="lg"
                          fit="cover"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            border: "1px solid #E5E7EB",
                          }}
                        />
                      </Box>
                    ))}
                </Flex>
                <Text size="xs" c="slate.6" mt="xs">
                  Facility photos and product images visible after verification
                </Text>
              </Box>
            )}
        </Flex>
        </Box>
      </Paper>

      {/* Products & Services */}
      <ProductsServicesTab company={company} isMatchedView={true} />

      {/* Capabilities & Offerings */}
      <Paper
        p={0}
        radius="lg"
        mb="xl"
        style={{
          overflow: "hidden",
          backgroundColor: "#ebf0ff",
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Flex
          align="center"
          gap="sm"
          px={{ base: "md", sm: "xl" }}
          py={{ base: "md", sm: "lg" }}
          style={{ backgroundColor: "#04305c" }}
        >
          <IconTool size={22} color="#ffc300" stroke={1.5} style={{ transform: "scaleX(-1)" }} />
          <Text fw={700} fz="md" c="#ffc300" style={{ letterSpacing: 0.2 }}>
            Capabilities & Offerings
          </Text>
        </Flex>
        <Box p={{ base: "lg", sm: "xl" }}>
        <Flex direction="column" gap="lg">
          {/* Core Capabilities */}
          <Box>
            <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
              CORE CAPABILITIES
            </Text>
            <Flex gap="xs" wrap="wrap">
              {(company.coreCapabilities || []).map((capability: string, index: number) => (
                <Badge key={index} style={{ backgroundColor: "#04305c", color: "white" }} py={14} px={12} size="md" fw={500}>
                  {capability}
                </Badge>
              ))}
              {(!company.coreCapabilities || company.coreCapabilities.length === 0) && (
                <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                  N/A
                </Text>
              )}
            </Flex>
          </Box>

          {/* Secondary Capabilities */}
          {company.secondaryCapabilities && company.secondaryCapabilities.length > 0 && (
            <Box>
              <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                SECONDARY CAPABILITIES
              </Text>
              <Flex gap="xs" wrap="wrap">
                {company.secondaryCapabilities.map((capability: string, index: number) => (
                  <Badge key={index} style={{ backgroundColor: "#04305c", color: "white" }} py={14} px={12} size="md" fw={500}>
                    {capability}
                  </Badge>
                ))}
              </Flex>
            </Box>
          )}

          {/* Key Differentiators */}
          {company.keyDifferentiators && (
            <Box>
              <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                KEY DIFFERENTIATORS
              </Text>
              <Text size="sm">{company.keyDifferentiators}</Text>
            </Box>
          )}

          {/* Technology & Infrastructure */}
          <Box>
            <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
              TECHNOLOGY & INFRASTRUCTURE
            </Text>
            <Flex gap="xs" wrap="wrap">
              {(company.technologies || []).map((tech: string, index: number) => (
                <Badge key={index} style={{ backgroundColor: "#04305c", color: "white" }} py={14} px={12} size="md" fw={500}>
                  {tech}
                </Badge>
              ))}
              {(!company.technologies || company.technologies.length === 0) && (
                <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                  N/A
                </Text>
              )}
            </Flex>
          </Box>

          {/* Additional Technology & Infrastructure */}
          {company.additionalTechInfrastructure && company.additionalTechInfrastructure.length > 0 && (
            <Box>
              <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                ADDITIONAL TECHNOLOGY & INFRASTRUCTURE
              </Text>
              <Flex gap="xs" wrap="wrap">
                {company.additionalTechInfrastructure.map((item: string, index: number) => (
                  <Badge key={index} style={{ backgroundColor: "#04305c", color: "white" }} py={14} px={12} size="md" fw={500}>
                    {getAdditionalTechInfraLabel(item)}
                  </Badge>
                ))}
              </Flex>
            </Box>
          )}
        </Flex>
        </Box>
      </Paper>

      {/* Geography & Markets */}
      <Paper
        p={0}
        radius="lg"
        mb="xl"
        style={{
          overflow: "hidden",
          backgroundColor: "#ebf0ff",
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Flex
          align="center"
          gap="sm"
          px={{ base: "md", sm: "xl" }}
          py={{ base: "md", sm: "lg" }}
          style={{ backgroundColor: "#04305c" }}
        >
          <IconMapPin size={22} color="#ffc300" stroke={1.5} />
          <Text fw={700} fz="md" c="#ffc300" style={{ letterSpacing: 0.2 }}>
            Geography & Markets
          </Text>
        </Flex>
        <Box p={{ base: "lg", sm: "xl" }}>
        <Flex gap="xl" wrap="wrap">
          {/* Operating GEOGRAPHIES */}
          <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
            <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
              OPERATING GEOGRAPHIES
            </Text>
            <Flex gap="xs" wrap="wrap">
              {(company.operatingCountries || [])
                .map((country: string) => displayHeadquartersText(country))
                .filter((label: string) => label !== "N/A")
                .map((country: string, index: number) => (
                  <Badge
                    key={index}
                    style={{ backgroundColor: "#04305c", color: "white" }}
                    py={14}
                    px={12}
                    size="md"
                    fw={500}
                  >
                    {country}
                  </Badge>
                ))}
              {(!company.operatingCountries ||
                company.operatingCountries.length === 0 ||
                (company.operatingCountries || []).every(
                  (c: string) => displayHeadquartersText(c) === "N/A",
                )) && (
                <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                  N/A
                </Text>
              )}
            </Flex>
          </Flex>

          {/* Preferred Collaboration Regions */}
          <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
            <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
              PREFERRED COLLABORATION REGIONS
            </Text>
            <Flex gap="xs" wrap="wrap">
              {(company.preferredCollaborationRegions || []).map((region: string, index: number) => (
                <Badge key={index} style={{ backgroundColor: "#04305c", color: "white" }} py={14} px={12} size="md" fw={500}>
                  {getRegionLabel(region)}
                </Badge>
              ))}
              {(!company.preferredCollaborationRegions || company.preferredCollaborationRegions.length === 0) && (
                <Text  size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                  N/A
                </Text>
              )}
            </Flex>
          </Flex>
        </Flex>
        </Box>
      </Paper>

      {/* Contact Person Details */}
      <Paper
        p={0}
        radius="lg"
        mb="xl"
        style={{
          overflow: "hidden",
          backgroundColor: "#ebf0ff",
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Flex
          align="center"
          gap="sm"
          px={{ base: "md", sm: "xl" }}
          py={{ base: "md", sm: "lg" }}
          style={{ backgroundColor: "#04305c" }}
        >
          <IconUser size={22} color="#ffc300" stroke={1.5} />
          <Text fw={700} fz="md" c="#ffc300" style={{ letterSpacing: 0.2 }}>
            Contact Person
          </Text>
        </Flex>
        <Box p={{ base: "lg", sm: "xl" }}>
        <Box
          pt="md"
          pb="md"
          style={{
            borderTop: "1px solid #E5E7EB",
            borderBottom: "1px solid #E5E7EB",
          }}
        >
          <Flex gap="md" wrap="wrap">
            <Flex direction="column" style={{ flex: "1 1 220px", minWidth: "220px" }}>
              <Text  size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                NAME
              </Text>
              <Text size="sm">{company.user?.name || "N/A"}</Text>
            </Flex>
            <Flex direction="column" style={{ flex: "1 1 220px", minWidth: "220px" }}>
              <Text  size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                PHONE NUMBER
              </Text>
              <Text size="sm">{company.user?.phoneNumber || "N/A"}</Text>
            </Flex>
            <Flex direction="column" style={{ flex: "1 1 220px", minWidth: "220px" }}>
              <Text  size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                DESIGNATION
              </Text>
              <Text size="sm">{company.user?.designation || "N/A"}</Text>
            </Flex>
          </Flex>
        </Box>
        </Box>
      </Paper>

      {/* System Verification Details */}
      {metadata && (
        <Paper
          p={0}
          radius="lg"
          mb="xl"
          style={{
            overflow: "hidden",
            backgroundColor: "#ebf0ff",
            boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Flex
            align="center"
            gap="sm"
            px={{ base: "md", sm: "xl" }}
            py={{ base: "md", sm: "lg" }}
            style={{ backgroundColor: "#04305c" }}
          >
            <IconShieldCheck size={22} color="#ffc300" stroke={1.5} />
            <Text fw={700} fz="md" c="#ffc300" style={{ letterSpacing: 0.2 }}>
              System Verification Details
            </Text>
          </Flex>
          <Box p={{ base: "lg", sm: "xl" }}>
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing={{ base: "lg", sm: "xl" }} verticalSpacing="lg">
              <Stack gap="xs">
                <Text size="md" c="slate.8" tt="uppercase" fw={600}>
                  COMPANY NAME
                </Text>
                <Text size="sm">{metadata.companyName || "-"}</Text>
              </Stack>
              <Stack gap="xs">
                <Text size="md" c="slate.8" tt="uppercase" fw={600}>
                  COMPANY ADDRESS
                </Text>
                <Text size="sm">{metadata.companyAddress || "-"}</Text>
              </Stack>
              <Stack gap="xs">
                <Text size="md" c="slate.8" tt="uppercase" fw={600}>
                  DATE OF INCORPORATION
                </Text>
                <Text size="sm">{metadata.dateOfIncorporation || "-"}</Text>
              </Stack>
              <Stack gap="xs">
                <Text size="md" c="slate.8" tt="uppercase" fw={600}>
                  REGISTRATION NUMBER
                </Text>
                <Text size="sm">{metadata.registrationNumber || "-"}</Text>
              </Stack>
              <Stack gap="xs">
                <Text size="md" c="slate.8" tt="uppercase" fw={600}>
                  COMPANY TYPE
                </Text>
                <Text size="sm">{metadata.companyType || "-"}</Text>
              </Stack>
              <Stack gap="xs">
                <Text size="md" c="slate.8" tt="uppercase" fw={600}>
                  COMPANY STATUS
                </Text>
                <Flex align="center" wrap="wrap">
                  <Text
                    size="sm"
                    fw={500}
                    c={(metadata.companyStatus || "").toLowerCase() === "active" ? "green.7" : "red.7"}
                  >
                    {metadata.companyStatus || "-"}
                  </Text>
                </Flex>
              </Stack>
            </SimpleGrid>
          </Box>
        </Paper>
      )}

    </>
  );
}
